import { isEmpty } from "lodash-es";
import { useMemo } from "react";
import { useSupabaseLoader } from "../../../../../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../../../../../server/supabase/hooks/types";
import { Waiting } from "../../../../../../../server/supabase/hooks/use-supabase-loader";
import type { SerialisedLexicalEditorDesign } from "../../../../../../message/editor/types/serialised-editor-design";
import {
  type MomentPreviewTags,
  SupabaseMomentPreviewService,
} from "../../../../../server/supabase-preview-service";
import type { PreviewMomentData } from "../types";
import type { MomentPreviewInfo } from "./use-preview-info";

type Config = { loading: true } | { loading: false; additional_merge_fields: string[] };

type Params = {
  dynamic_segment_id_map: PreviewMomentData["dynamic_segment_id_map"];
  preview_info: UseSupabaseLoaderState<MomentPreviewInfo | null>;
  preview_test_data: UseSupabaseLoaderState<MomentPreviewTags | null>;
  design: SerialisedLexicalEditorDesign | null;
  subject: string | null;
  email_additional_merge_fields: string[];
  use_unlayer_email_preview: boolean;
  config?: Config;
};

export function usePreviewTags({
  dynamic_segment_id_map,
  preview_info: { loading: loadingPreviewInfo, data: previewInfo },
  preview_test_data: { loading: loadingPreviewTestData, data: previewTestData },
  design,
  subject,
  email_additional_merge_fields,
  use_unlayer_email_preview,
  config,
}: Params): UseSupabaseLoaderState<{
  tags: MomentPreviewTags;
  dynamic_condition_fields: Record<string, boolean>;
}> {
  const dynamic_condition_fields = useMemo(() => {
    if (!previewInfo || isEmpty(dynamic_segment_id_map)) return {};

    const subjectId = previewInfo.selectedPreviewInfo?.subject.id;

    return Object.keys(dynamic_segment_id_map)
      .map((key) => {
        const info = previewInfo.dynamicSegmentRecipients[key];
        if (!info) return { key, value: false };

        const isDynamic = info.data.some((item) => subjectId && item.id === subjectId);

        return { key: info.cleanSegmentId, value: isDynamic };
      })
      .reduce(
        (acc, { key, value }) => {
          acc[key] = value;
          return acc;
        },
        {} as Record<string, boolean>,
      );
  }, [previewInfo, dynamic_segment_id_map]);

  return useSupabaseLoader(
    async ({ supabase }) => {
      const state: Config =
        config === undefined ? { loading: false, additional_merge_fields: [] } : config;

      if (state.loading || loadingPreviewInfo || loadingPreviewTestData) throw new Waiting();
      if (!design && !use_unlayer_email_preview) return { tags: [], dynamic_condition_fields: {} };

      if (!previewInfo?.selectedPreviewInfo)
        return { tags: previewTestData ?? [], dynamic_condition_fields };

      const response = await new SupabaseMomentPreviewService(supabase).getMomentPreview(
        previewInfo.selectedPreviewInfo.subject.id,
        previewInfo.selectedPreviewInfo.recipient.id,
        design,
        subject,
        state.additional_merge_fields.concat(email_additional_merge_fields),
      );

      if (response.error) {
        console.error(response.error);
        return { tags: [], dynamic_condition_fields };
      }

      return { tags: response.data, dynamic_condition_fields };
    },
    [
      loadingPreviewInfo,
      loadingPreviewTestData,
      design,
      previewInfo?.selectedPreviewInfo,
      previewTestData,
      dynamic_condition_fields,
      subject,
      config,
      email_additional_merge_fields,
      use_unlayer_email_preview,
    ],
    "public",
  );
}
